import React, { useState, useEffect, useMemo } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import Breadcrumb from '../components/Breadcrumb';
import { CardGridSkeleton, EmptyState } from '../components/ui';

const BRAND_META = {
  'Rolex':           { wm:'ROLEX', sym:'♔', col:'#0a3d2e', tc:'#d9b771' },
  'Patek Philippe':  { wm:'PATEK PHILIPPE', sym:'✛', col:'#13294b', tc:'#c9a24b' },
  'Audemars Piguet': { wm:'AP', sym:'', col:'#141414', tc:'#e8e3d6' },
  'Cartier':         { wm:'Cartier', sym:'', col:'#7a1420', tc:'#e8d6b0' },
};
const BRAND_ORDER = ['Rolex', 'Patek Philippe', 'Audemars Piguet', 'Cartier'];

const MD_SUMMARIES = [
  { t:'🗺️ 00_KNOWLEDGE_MAP.md', d:'Mappa di auto-orientamento: stato di copertura per brand/modello (todo → skeleton → info → verificato+media). Rolex avanzato, Patek in espansione, Cartier e AP avviati. Market layer Rolex completo (≥2 fonti datate).' },
  { t:'📇 00_WATCH_INDEX.md', d:'Indice del database: registro delle pagine-referenza HTML per brand → modello → ref, con struttura cartelle, regole e collegamento a SOURCES_FEED e TEMPLATES.' },
];

// Label compatte per i campi stato
const STATO_LABELS = {
  icona:     'Icona',
  foto_reali:'Foto',
  market:    'Market',
  corredo:   'Corredo',
};

const metaFor = (brand) => BRAND_META[brand] || { wm:(brand||'').toUpperCase(), sym:'', col:'#1c2230', tc:'#cbd5e1' };

function modelloDaPath(path_html, fallback) {
  if (!path_html) return fallback || '—';
  const parts = path_html.split('/').filter(Boolean);
  const bi = parts.indexOf('Brands');
  if (bi !== -1 && parts.length > bi + 2) return parts[bi + 2].replace(/-/g, ' ');
  if (parts.length >= 2) return parts[parts.length - 2].replace(/-/g, ' ');
  return fallback || '—';
}

function hrefScheda(s) {
  if (s.url) return s.url;
  if (s.path_html) {
    const brandsIdx = s.path_html.indexOf('Brands/');
    if (brandsIdx !== -1) return '/api/pep_schede/html/' + s.path_html.substring(brandsIdx);
    return '/api/pep_schede/html/' + s.path_html;
  }
  return null;
}

// URL icona: usa icona_url dal DB se presente, altrimenti deriva dal brand+ref
function iconSrc(s, brand) {
  if (s.icona_url) return '/api/pep_schede/html/' + s.icona_url;
  // fallback derivato: Brands/{Brand}/_icons/{ref}.png
  return `/api/pep_schede/html/Brands/${encodeURIComponent(brand)}/_icons/${encodeURIComponent(s.referenza)}.png`;
}

// Data formattata compatta: "3 giu '25"
function fmtDate(d) {
  if (!d) return null;
  return new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day:'numeric', month:'short', year:'2-digit' });
}

// Badges avanzamento dal campo stato (JSONB)
function StatoBadges({ stato }) {
  if (!stato || typeof stato !== 'object' || Object.keys(stato).length === 0) return null;
  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:3, marginTop:6 }}>
      {Object.entries(stato).map(([k, v]) => {
        const isDone = v === true || (Array.isArray(v) && v.length > 0);
        const count  = Array.isArray(v) ? v.length : null;
        const label  = STATO_LABELS[k] || k.charAt(0).toUpperCase() + k.slice(1);
        return (
          <span key={k} style={{
            fontSize:9.5, padding:'1px 6px', borderRadius:4, fontWeight:500,
            background: isDone ? 'var(--green-dim)' : 'var(--surface)',
            color:      isDone ? 'var(--green)'     : 'var(--text-dim)',
            border:     `1px solid ${isDone ? 'rgba(16,185,129,0.25)' : 'var(--border)'}`,
          }}>
            {isDone ? '✓' : '○'} {label}{count ? ` ×${count}` : ''}
          </span>
        );
      })}
    </div>
  );
}

function BrandLogo({ brand, height = 56, fontSize = 15 }) {
  const m = metaFor(brand);
  return (
    <div style={{
      display:'flex', alignItems:'center', justifyContent:'center', gap:7,
      padding:'0 12px', borderRadius:8, fontWeight:600, letterSpacing:'0.1em',
      fontFamily:"Georgia,'Times New Roman',serif", fontSize, textAlign:'center',
      height, background:m.col, color:m.tc,
    }}>
      {m.wm}{m.sym ? ' ' + m.sym : ''}
    </div>
  );
}

// Icona referenza: icona_url dal DB → fallback derivato → emoji ⌚
function SchedaIcon({ scheda, brand }) {
  const [src, setSrc] = useState(() => iconSrc(scheda, brand));
  const [fallback, setFallback] = useState(false);

  // Reset quando cambia la scheda
  useEffect(() => {
    setSrc(iconSrc(scheda, brand));
    setFallback(false);
  }, [scheda.id, scheda.icona_url]);

  const handleError = () => {
    // Se stava usando icona_url e fallisce, prova il path derivato
    if (scheda.icona_url && src === '/api/pep_schede/html/' + scheda.icona_url) {
      const derived = `/api/pep_schede/html/Brands/${encodeURIComponent(brand)}/_icons/${encodeURIComponent(scheda.referenza)}.png`;
      if (derived !== src) { setSrc(derived); return; }
    }
    setFallback(true);
  };

  return (
    <div style={{ width:'100%', height:150, display:'flex', alignItems:'center', justifyContent:'center' }}>
      {fallback
        ? <span style={{ fontSize:44 }}>⌚</span>
        : <img src={src} alt={scheda.referenza} onError={handleError}
               style={{ width:'100%', height:150, objectFit:'contain' }}/>}
    </div>
  );
}

export default function PepSchede() {
  const { user } = useAuth();
  const [schede, setSchede] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nav, setNav] = useState({ brand:null, modello:null });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ brand:'', referenza:'', titolo:'', url:'', path_html:'' });
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => { loadSchede(); }, []);

  const loadSchede = () => {
    setLoading(true);
    api.get('/pep_schede')
      .then(r => { setSchede(Array.isArray(r.data) ? r.data : []); setLoading(false); })
      .catch(() => { setSchede([]); setLoading(false); });
  };

  const tree = useMemo(() => {
    const t = {};
    for (const s of schede) {
      const brand = s.brand || '—';
      const modello = modelloDaPath(s.path_html, s.titolo);
      if (!t[brand]) t[brand] = {};
      if (!t[brand][modello]) t[brand][modello] = [];
      t[brand][modello].push(s);
    }
    return t;
  }, [schede]);

  const brands = useMemo(() => {
    const present = Object.keys(tree);
    const ordered = BRAND_ORDER.filter(b => present.includes(b));
    const extra = present.filter(b => !BRAND_ORDER.includes(b)).sort();
    return [...ordered, ...extra];
  }, [tree]);

  const syncVault = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await api.post('/pep_schede/sync');
      setSyncResult(r.data);
      if (r.data.inseriti > 0) loadSchede();
    } catch (e) {
      setSyncResult({ error: e.response?.data?.detail || 'Errore sync' });
    }
    setSyncing(false);
  };

  const submit = async () => {
    if (!form.brand.trim() || !form.referenza.trim()) return;
    try {
      await api.post('/pep_schede', form);
      setShowForm(false);
      setForm({ brand:'', referenza:'', titolo:'', url:'', path_html:'' });
      loadSchede();
    } catch {}
  };

  const cardBase = {
    background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12,
    padding:'14px 16px', cursor:'pointer', transition:'border-color 0.15s, transform 0.1s',
  };
  const hoverOn  = (e,c) => { e.currentTarget.style.borderColor=c+'66'; e.currentTarget.style.transform='translateY(-2px)'; };
  const hoverOff = (e)   => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.transform='none'; };
  const link = { color:'var(--cyan)', cursor:'pointer', textDecoration:'none' };

  return (
    <div className="eden-page">
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:18 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:700, margin:'0 0 4px', color:'var(--text)' }}>Schede Pep</h1>
          <p style={{ fontSize:13, color:'var(--text-muted)', margin:0 }}>
            Archivio orologi diviso per brand · {schede.length} schede
          </p>
        </div>
        {user?.ruolo === 'admin' && (
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            {syncResult && (
              <span style={{ fontSize:11.5, color: syncResult.error ? 'var(--red)' : 'var(--green)' }}>
                {syncResult.error
                  ? `✗ ${syncResult.error}`
                  : syncResult.inseriti > 0
                    ? `✓ +${syncResult.inseriti} schede aggiunte`
                    : '✓ Già in sync'}
              </span>
            )}
            <button onClick={syncVault} disabled={syncing}
              style={{ fontSize:12, display:'flex', alignItems:'center', gap:5, padding:'7px 13px',
                background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8,
                color:'var(--text-muted)', cursor: syncing?'not-allowed':'pointer',
                opacity: syncing ? 0.6 : 1 }}>
              <RefreshCw size={13} style={{ animation: syncing ? 'spin 1s linear infinite' : 'none' }}/>
              {syncing ? 'Sync…' : 'Sincronizza vault'}
            </button>
            <button onClick={() => setShowForm(v => !v)} className="eden-btn-primary" style={{ fontSize:12 }}>
              <Plus size={14}/> Aggiungi scheda
            </button>
          </div>
        )}
      </div>

      {showForm && user?.ruolo === 'admin' && (
        <div style={{ background:'var(--surface)', border:'1px solid var(--cyan-dim)', borderRadius:12, padding:'18px 20px', marginBottom:20 }}>
          <div style={{ fontSize:14, fontWeight:600, marginBottom:14 }}>Aggiungi scheda</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
            <input placeholder="Brand *" value={form.brand} onChange={e=>setForm(f=>({...f,brand:e.target.value}))} className="eden-input"/>
            <input placeholder="Referenza *" value={form.referenza} onChange={e=>setForm(f=>({...f,referenza:e.target.value}))} className="eden-input"/>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
            <input placeholder="Titolo" value={form.titolo} onChange={e=>setForm(f=>({...f,titolo:e.target.value}))} className="eden-input"/>
            <input placeholder="path_html (es. .../Brands/Rolex/Daytona/6239.html)" value={form.path_html} onChange={e=>setForm(f=>({...f,path_html:e.target.value}))} className="eden-input"/>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={submit} className="eden-btn-primary" style={{ fontSize:12 }}>Salva</button>
            <button onClick={() => setShowForm(false)} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:7, color:'var(--text-muted)', cursor:'pointer', padding:'7px 14px', fontSize:12 }}>Annulla</button>
          </div>
        </div>
      )}

      {loading ? (
        <CardGridSkeleton count={9} />
      ) : schede.length === 0 ? (
        <EmptyState icon={Plus} title="Nessuna scheda" hint="Nessuna scheda nel database. Aggiungine una per iniziare." />
      ) : nav.brand === null ? (

        /* ─── LIVELLO 1 · BRAND ─────────────────────────────────────────── */
        <>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:22 }}>
            {MD_SUMMARIES.map(md => (
              <div key={md.t} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'12px 14px' }}>
                <div style={{ fontWeight:600, fontSize:12.5, marginBottom:4, color:'var(--text)' }}>{md.t}</div>
                <div style={{ fontSize:11.5, lineHeight:1.5, color:'var(--text-muted)' }}>{md.d}</div>
              </div>
            ))}
          </div>
          <p style={{ fontSize:11, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-dim)', margin:'0 0 10px' }}>Brand</p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:12 }}>
            {brands.map(brand => {
              const models = tree[brand];
              const nModels = Object.keys(models).length;
              const nSchede = Object.values(models).reduce((a, arr) => a + arr.length, 0);
              // Conta schede aggiornate di recente (aggiornata_il presente)
              const nAggiornate = Object.values(models).flat().filter(s => s.aggiornata_il).length;
              const c = metaFor(brand).col;
              return (
                <div key={brand} style={cardBase}
                     onMouseEnter={e=>hoverOn(e,c)} onMouseLeave={hoverOff}
                     onClick={() => setNav({ brand, modello:null })}>
                  <BrandLogo brand={brand} height={56} />
                  <div style={{ fontWeight:600, fontSize:14, marginTop:8, color:'var(--text)' }}>{brand}</div>
                  <div style={{ fontSize:12, color:'var(--text-muted)' }}>{nModels} modelli · {nSchede} schede</div>
                  {nAggiornate > 0 && (
                    <div style={{ fontSize:10.5, color:'var(--green)', marginTop:3 }}>✓ {nAggiornate} aggiornate</div>
                  )}
                </div>
              );
            })}
          </div>
        </>

      ) : nav.modello === null ? (

        /* ─── LIVELLO 2 · MODELLI ───────────────────────────────────────── */
        <>
          <Breadcrumb items={[
            { label: 'Schede Pep', onClick: () => setNav({ brand:null, modello:null }) },
            { label: nav.brand },
          ]} />
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
            <BrandLogo brand={nav.brand} height={40} fontSize={13} />
            <h1 style={{ margin:0, fontSize:20, color:'var(--text)' }}>{nav.brand}</h1>
          </div>
          <p style={{ fontSize:11, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-dim)', margin:'0 0 10px' }}>Modelli</p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:12 }}>
            {Object.keys(tree[nav.brand]).sort().map(modello => {
              const schede_mod = tree[nav.brand][modello];
              const nAgg = schede_mod.filter(s => s.aggiornata_il).length;
              // Ultima data di aggiornamento del modello
              const dates = schede_mod.map(s => s.aggiornata_il).filter(Boolean).sort();
              const lastDate = dates.length ? dates[dates.length - 1] : null;
              const c = metaFor(nav.brand).col;
              return (
                <div key={modello} style={cardBase}
                     onMouseEnter={e=>hoverOn(e,c)} onMouseLeave={hoverOff}
                     onClick={() => setNav({ brand:nav.brand, modello })}>
                  <div style={{ fontWeight:600, fontSize:14, color:'var(--text)' }}>{modello}</div>
                  <div style={{ fontSize:12, color:'var(--text-muted)' }}>{schede_mod.length} schede</div>
                  {lastDate && (
                    <div style={{ fontSize:10.5, color:'var(--text-dim)', marginTop:3 }}>
                      Agg. {fmtDate(lastDate)}
                      {nAgg < schede_mod.length && <span style={{ color:'var(--text-dim)' }}> · {nAgg}/{schede_mod.length}</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>

      ) : (

        /* ─── LIVELLO 3 · SCHEDE (griglia) ─────────────────────────────── */
        <>
          <Breadcrumb items={[
            { label: 'Schede Pep', onClick: () => setNav({ brand:null, modello:null }) },
            { label: nav.brand, onClick: () => setNav({ brand:nav.brand, modello:null }) },
            { label: nav.modello },
          ]} />
          <h1 style={{ fontSize:20, color:'var(--text)', margin:'0 0 4px' }}>{nav.brand} · {nav.modello}</h1>
          <p style={{ fontSize:12.5, color:'var(--text-muted)', margin:'0 0 16px' }}>
            Schede-referenza · clicca per aprire la scheda HTML
          </p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(158px,1fr))', gap:8 }}>
            {tree[nav.brand][nav.modello]
              .slice().sort((a,b) => (a.referenza||'').localeCompare(b.referenza||''))
              .map(s => {
                const href = hrefScheda(s);
                const brandColor = metaFor(nav.brand).col;

                const cardContent = (
                  <>
                    {/* Icona PNG dal vault — via backend, auto mime-type */}
                    <SchedaIcon scheda={s} brand={nav.brand} />

                    {/* Referenza */}
                    <div style={{ fontWeight:700, fontSize:13.5, marginTop:8, color:'var(--text)' }}>
                      {s.referenza}
                    </div>

                    {/* Titolo (se presente) */}
                    {s.titolo && (
                      <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2, lineHeight:1.4 }}>
                        {s.titolo}
                      </div>
                    )}

                    {/* Data aggiornamento */}
                    {s.aggiornata_il && (
                      <div style={{ fontSize:10, color:'var(--text-dim)', marginTop:3 }}>
                        {fmtDate(s.aggiornata_il)}
                      </div>
                    )}

                    {/* Badge stato */}
                    <StatoBadges stato={s.stato} />

                    {/* Link apertura */}
                    {href && (
                      <div style={{ fontSize:11, color:'var(--cyan)', marginTop:6 }}>apri ↗</div>
                    )}
                  </>
                );

                const cardStyle = {
                  ...cardBase,
                  padding:'8px 8px 10px',
                  textAlign:'center',
                  display:'block',
                  textDecoration:'none',
                  color:'inherit',
                };

                return href ? (
                  <a key={s.id || s.referenza} href={href} target="_blank" rel="noopener noreferrer"
                     style={cardStyle}
                     onMouseEnter={e=>hoverOn(e,brandColor)} onMouseLeave={hoverOff}>
                    {cardContent}
                  </a>
                ) : (
                  <div key={s.id || s.referenza} style={{ ...cardStyle, cursor:'default' }}>
                    {cardContent}
                  </div>
                );
              })}
          </div>
        </>
      )}
    </div>
  );
}
